import { IonContent, IonSpinner } from '@ionic/react';
import React from 'react';

import { ReviewCards } from '@/components/ReviewCards';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { AnimationFill } from '@/pages/Onboarding/components/AnimationFill';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle } from '../../components/Title';
import { layoutQueryParams } from '../../params';

import { ScreenSettingUpProfileStyles } from './SettingUpProfile.styles';

const animationSteps = [
  {
    delay: 0,
    label: 'Applying your preferences',
  },
  {
    delay: 3,
    label: 'Setup AI personality',
  },
  {
    delay: 6,
    label: 'Initializing LLM modules',
  },
];

export const ScreenSettingUpProfile: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'SettingUpProfile',
      });
    }
    layoutQueryParams.removeStepsData();
  }, []);

  const { refreshUser } = useAccount();

  const shouldAnimate = webCampaign.isAnimated();

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  const handleFinishSettings = async () => {
    const shouldCreateStripeClient = webCampaign.setup.paywall !== null;

    Promise.all([
      shouldCreateStripeClient && createStripeClient(),
      userService.createOrUpdate({
        onboarding_data: {
          onboarded: true,
        },
      }),
    ])
      .then(() => {
        trackOnboardingComplete();
        refreshUser();
      })
      .catch((error) => {
        console.error('Error while creating stripe client', error);
      });
  };

  return (
    <ScreenSettingUpProfileStyles background="dark-violet">
      <OwnAiTitle className="setting-up-title">
        Creating a perfect partner
      </OwnAiTitle>
      <OnboardingSubtitle className="setting-up-subtitle">
        It will take less than one minute
      </OnboardingSubtitle>

      <IonContent>
        <AnimationFill
          show
          onComplete={handleFinishSettings}
          animate={shouldAnimate}
          steps={animationSteps}
        />

        <section className="setting-up-user-reviews">
          <UiTypography variant="title-large" align="center" transform="bold">
            Trusted by 1,556,867 users
          </UiTypography>
          <ReviewCards reverseList color="dark-violet" />
        </section>
      </IonContent>

      {isCreatingStripeClient && <IonSpinner name="bubbles" />}
    </ScreenSettingUpProfileStyles>
  );
};
